// External libraries
import { ApolloLink, Observable } from 'apollo-link';
import { onError } from 'apollo-link-error';

// Internal libraries
import { clearAccessToken, setAccessToken } from '@/lib/globals';
import { getAccessTokenType, isAccountCreationMutation } from '../utils';

const link = new ApolloLink((operation, forward) => {
  const { operationName } = operation;

  // Clear existing access token so that new guest account
  // creation is unaffected.
  if (isAccountCreationMutation(operationName)) {
    clearAccessToken();
  }

  return new Observable(observer => {
    const subscription = forward(operation).subscribe({
      complete: observer.complete.bind(observer),
      error: observer.error.bind(observer),
      next: response => {
        // Set the access token in the case of a successful account creation
        if (isAccountCreationMutation(operationName)) {
          const buyer = response?.data?.onboardAccount?.buyer;
          if (buyer?.auth?.accessToken) {
            setAccessToken({
              type: getAccessTokenType(operationName),
              value: buyer.auth.accessToken,
            });
          }
        }

        observer.next(response);
      },
    });

    return () => subscription?.unsubscribe?.();
  });
});

/**
 * In the case of a post-account creation error on the SignUpNewMemberMutation
 * we need to set the access token since an account was created and the user
 * will not be able to re-submit the form. Instead we will use the access token
 * to drop a login cookie and then redirect the user to the member flow.
 */
const errorLink = onError(({ graphQLErrors, operation }) => {
  const { operationName } = operation;
  const { errorData } = graphQLErrors?.[0] || {};

  // If the error occurred after successful account creation,
  // set the access token.
  if (isAccountCreationMutation(operationName) && errorData?.accessToken) {
    setAccessToken({
      type: getAccessTokenType(operationName),
      value: errorData.accessToken,
    });
  }
});

export const authLink = link.concat(errorLink);
